/* SAVID product page controller - FULLY FUNCTIONAL.
 *
 * Every selection resolves to a real Shopify variant id and adds real
 * line items to the real cart. Bundle pricing comes from native Shopify
 * automatic discounts, so the cart total is always server-authoritative.
 */
import { addBundleToCart } from '@theme/savid-bundle-cart';

function boot() {
  const pdp = document.querySelector('[data-savid-pdp]');
  if (!pdp || pdp.dataset.savidBound === '1') return;
  pdp.dataset.savidBound = '1';

  let variants = [];
  try {
    const holder = pdp.querySelector('[data-savid-variants]');
    variants = holder ? JSON.parse(holder.textContent) : [];
  } catch (e) {
    variants = [];
  }

  /* one shared message on the product page; one per card on the
     bundles page - `say` writes to whichever is in scope. */
  const sharedMsg = pdp.querySelector('[data-savid-msg]:not([data-savid-tier] [data-savid-msg])');
  const priceEl = pdp.querySelector('[data-savid-price]');
  const compareEl = pdp.querySelector('[data-savid-compare]');
  const baseCompare = compareEl ? compareEl.textContent.trim() : '';
  const baseCompareShown = compareEl ? !compareEl.hidden : false;
  const addBtn = pdp.querySelector('[data-savid-add]');
  const single = pdp.querySelector('[data-savid-single]');
  const tiers = Array.prototype.slice.call(pdp.querySelectorAll('[data-savid-tier]'));
  const sticky = document.querySelector('[data-savid-sticky]');

  const find = (color, size) => {
    for (let i = 0; i < variants.length; i += 1) {
      if (variants[i].color === color && variants[i].size === size) return variants[i];
    }
    return null;
  };
  const exists = (color, size) => !!find(color, size);
  const sellable = (color, size) => {
    const v = find(color, size);
    return !!(v && v.available);
  };

  function msgFor(scope) {
    if (scope) {
      const local = scope.querySelector('[data-savid-msg]');
      if (local) return local;
    }
    return sharedMsg;
  }

  function say(text, kind, scope) {
    const el = msgFor(scope);
    if (!el) return;
    el.textContent = text || '';
    el.classList.toggle('is-error', kind === 'error');
    el.classList.toggle('is-ok', kind === 'ok');
  }

  function clearAllMessages() {
    pdp.querySelectorAll('[data-savid-msg]').forEach(function (el) {
      el.textContent = '';
      el.classList.remove('is-error', 'is-ok');
    });
  }

  function setLabel(button, text) {
    const span = button.querySelector('[data-savid-btn-label]');
    if (span) span.textContent = text;
    else button.textContent = text;
  }
  function getLabel(button) {
    const span = button.querySelector('[data-savid-btn-label]');
    return span ? span.textContent : button.textContent;
  }

  /* Woo's bundles cards use dropdowns instead of swatches; both feed
     the same {color, size} state object. */
  function wireSelects(scope, onChange) {
    const state = { color: null, size: null };
    const colorSel = scope.querySelector('[data-savid-row-select="color"]');
    const sizeSel = scope.querySelector('[data-savid-row-select="size"]');

    function paintSizes() {
      if (!sizeSel) return;
      Array.prototype.forEach.call(sizeSel.options, function (opt) {
        if (!opt.value) return;
        const can = state.color ? sellable(state.color, opt.value) : true;
        opt.disabled = !can;
      });
      if (state.size && !sellable(state.color, state.size)) {
        state.size = null;
        sizeSel.value = '';
      }
    }

    if (colorSel) {
      colorSel.addEventListener('change', function () {
        state.color = colorSel.value || null;
        paintSizes();
        onChange(state);
      });
    }
    if (sizeSel) {
      sizeSel.addEventListener('change', function () {
        state.size = sizeSel.value || null;
        onChange(state);
      });
    }
    return state;
  }

  /* ---------- generic colour/size selector group ---------- */
  function wireGroup(scope, colorAttr, sizeAttr, onChange) {
    const state = { color: null, size: null };
    const colorBtns = Array.prototype.slice.call(scope.querySelectorAll('[' + colorAttr + ']'));
    const sizeBtns = Array.prototype.slice.call(scope.querySelectorAll('[' + sizeAttr + ']'));

    function paintSizes() {
      sizeBtns.forEach((b) => {
        const size = b.getAttribute(sizeAttr);
        if (!state.color) {
          b.classList.remove('is-unavailable', 'is-soldout');
          b.disabled = false;
          return;
        }
        const has = exists(state.color, size);
        const can = sellable(state.color, size);
        /* no such variant at all */
        b.classList.toggle('is-unavailable', !has);
        /* the variant exists but is not purchasable */
        b.classList.toggle('is-soldout', has && !can);
        b.disabled = !can;
        b.setAttribute('aria-disabled', can ? 'false' : 'true');
        if (!can && b.classList.contains('is-selected')) {
          b.classList.remove('is-selected');
          state.size = null;
        }
      });
    }

    colorBtns.forEach((b) => {
      b.addEventListener('click', (ev) => {
        ev.preventDefault();
        ev.stopPropagation();
        colorBtns.forEach((o) => {
          o.classList.remove('is-selected');
          o.setAttribute('aria-pressed', 'false');
        });
        b.classList.add('is-selected');
        b.setAttribute('aria-pressed', 'true');
        state.color = b.getAttribute(colorAttr);
        paintSizes();
        onChange(state);
      });
    });

    sizeBtns.forEach((b) => {
      b.addEventListener('click', (ev) => {
        ev.preventDefault();
        ev.stopPropagation();
        if (b.disabled) return;
        sizeBtns.forEach((o) => {
          o.classList.remove('is-selected');
          o.setAttribute('aria-pressed', 'false');
        });
        b.classList.add('is-selected');
        b.setAttribute('aria-pressed', 'true');
        state.size = b.getAttribute(sizeAttr);
        onChange(state);
      });
    });

    /* preselect the first colour so the size grid has a context */
    if (colorBtns.length) {
      colorBtns[0].classList.add('is-selected');
      colorBtns[0].setAttribute('aria-pressed', 'true');
      state.color = colorBtns[0].getAttribute(colorAttr);
      paintSizes();
    }
    return state;
  }

  /* ---------- single shirt ---------- */
  const singleState = single
    ? wireGroup(single, 'data-savid-color', 'data-savid-size', () => say('', null, null))
    : { color: null, size: null };

  /* ---------- per-shirt rows inside multi-shirt tiers ---------- */
  const rowStates = new Map();
  tiers.forEach((tier) => {
    tier.querySelectorAll('[data-savid-row]').forEach((row) => {
      const onChange = function () { say('', null, tier); };
      const usesSelects = row.querySelector('[data-savid-row-select]');
      rowStates.set(row, usesSelects
        ? wireSelects(row, onChange)
        : wireGroup(row, 'data-savid-row-color', 'data-savid-row-size', onChange));
    });
  });

  /* ---------- tier selection ---------- */
  /* ?bundle=1|3|5 lets the bundles page hand the customer straight to a
     preselected tier; otherwise the highlighted tier is the default. */
  let requested = null;
  try {
    const q = new URLSearchParams(window.location.search).get('bundle');
    if (q) requested = tiers.find((t) => t.getAttribute('data-shirts') === String(Number(q))) || null;
  } catch (e) {
    requested = null;
  }
  let active = requested || tiers.find((t) => t.classList.contains('is-highlight')) || tiers[0] || null;

  function selectTier(tier) {
    if (!tier) return;
    active = tier;
    tiers.forEach((t) => {
      const on = t === tier;
      t.classList.toggle('is-selected', on);
      const head = t.querySelector('[data-savid-tier-select]');
      if (head) head.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    const shirts = Number(tier.getAttribute('data-shirts') || 1);
    if (single) single.hidden = shirts > 1;
    const price = tier.getAttribute('data-tier-price') || '';
    if (priceEl && price) priceEl.textContent = price;
    if (compareEl) {
      const was = tier.getAttribute('data-tier-compare') || '';
      if (was) {
        compareEl.textContent = was;
        compareEl.hidden = false;
      } else {
        compareEl.textContent = baseCompare;
        compareEl.hidden = !baseCompareShown;
      }
    }
    if (sticky) {
      const st = sticky.querySelector('[data-savid-sticky-tier]');
      const sp = sticky.querySelector('[data-savid-sticky-price]');
      if (st) st.textContent = tier.getAttribute('data-tier-title') || '';
      if (sp) sp.textContent = price;
    }
  }

  tiers.forEach((tier) => {
    const head = tier.querySelector('[data-savid-tier-select]');
    if (head) head.addEventListener('click', () => selectTier(tier));
    tier.addEventListener('click', (ev) => {
      if (ev.target.closest('[data-savid-row]')) return;
      if (ev.target.closest('[data-savid-tier-select]')) return;
      selectTier(tier);
    });
  });
  selectTier(active);

  /* ---------- collect the current selection ---------- */
  const msgOne = pdp.getAttribute('data-msg-one');
  const msgMany = pdp.getAttribute('data-msg-many');

  function collect(tier) {
    const target = tier || active;
    if (!target) return { error: 'No bundle option is available.' };
    const shirts = Number(target.getAttribute('data-shirts') || 1);
    /* the bundles page carries Woo's own wording; the product page
       uses the more specific per-field messages below */
    const generic = shirts === 1 ? msgOne : msgMany;

    /* on the bundles page every tier, including the single-shirt one,
       carries its own per-shirt rows, so prefer them when present */
    const ownRows = target.querySelectorAll('[data-savid-row]');
    if (shirts === 1 && ownRows.length === 0) {
      if (!singleState.color) return { error: generic || 'Choose a color.' };
      if (!singleState.size) return { error: generic || 'Choose a size.' };
      const v = find(singleState.color, singleState.size);
      if (!v) return { error: singleState.color + ' is not made in size ' + singleState.size + '.' };
      if (!v.available) return { error: singleState.color + ' / ' + singleState.size + ' is sold out.' };
      return { items: [{ id: v.id, quantity: 1 }] };
    }

    const rows = Array.prototype.slice.call(target.querySelectorAll('[data-savid-row]'));
    const items = [];
    for (let i = 0; i < rows.length; i += 1) {
      const row = rows[i];
      const st = rowStates.get(row);
      const n = row.getAttribute('data-savid-row');
      if (!st || !st.color) return { error: generic || 'Choose a color for shirt #' + n + '.' };
      if (!st.size) return { error: generic || 'Choose a size for shirt #' + n + '.' };
      const v = find(st.color, st.size);
      if (!v) return { error: 'Shirt #' + n + ': ' + st.color + ' is not made in size ' + st.size + '.' };
      if (!v.available) return { error: 'Shirt #' + n + ': ' + st.color + ' / ' + st.size + ' is sold out.' };
      items.push({ id: v.id, quantity: 1 });
    }
    if (!items.length) return { error: 'Choose your shirts.' };
    return { items };
  }

  /* ---------- add to cart ---------- */
  let busy = false;
  async function doAdd(button) {
    if (busy) return;
    const tier = button.closest ? button.closest('[data-savid-tier]') : null;
    if (tier && tier !== active) selectTier(tier);
    const scope = tier;
    clearAllMessages();
    const picked = collect(tier);
    if (picked.error) {
      say(picked.error, 'error', scope);
      return;
    }
    busy = true;
    const label = getLabel(button);
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    setLabel(button, 'Adding...');

    let res;
    try {
      res = await addBundleToCart(picked.items, pdp);
    } catch (e) {
      res = { ok: false, reason: 'Could not add to cart. Please try again.' };
    }

    busy = false;
    button.disabled = false;
    button.removeAttribute('aria-busy');
    setLabel(button, label);

    if (!res || !res.ok) {
      say((res && res.reason) || 'Could not add to cart. Please try again.', 'error', scope);
    } else {
      const n = picked.items.reduce((a, it) => a + it.quantity, 0);
      say(n === 1 ? 'Added to cart.' : 'Added ' + n + ' shirts to cart.', 'ok', scope);
    }
  }

  pdp.querySelectorAll('[data-savid-add]').forEach(function (btn) {
    btn.addEventListener('click', function () { doAdd(btn); });
  });
  const stickyBtn = sticky ? sticky.querySelector('[data-savid-sticky-add]') : null;
  if (stickyBtn) stickyBtn.addEventListener('click', () => doAdd(stickyBtn));

  /* ---------- gallery ---------- */
  const thumbs = Array.prototype.slice.call(pdp.querySelectorAll('[data-savid-thumb]'));
  const slides = Array.prototype.slice.call(pdp.querySelectorAll('[data-savid-slide]'));
  thumbs.forEach((t) => {
    t.addEventListener('click', () => {
      const i = t.getAttribute('data-savid-thumb');
      thumbs.forEach((o) => o.classList.remove('is-active'));
      t.classList.add('is-active');
      slides.forEach((s) => s.classList.toggle('is-active', s.getAttribute('data-savid-slide') === i));
    });
  });

  /* ---------- sticky bar reveal ---------- */
  if (sticky && addBtn && typeof window.IntersectionObserver === 'function') {
    sticky.hidden = true;
    const io = new window.IntersectionObserver((entries) => {
      entries.forEach((en) => {
        sticky.hidden = en.isIntersecting;
      });
    }, { rootMargin: '0px 0px -80px 0px' });
    io.observe(addBtn);
  } else if (sticky) {
    sticky.hidden = false;
  }
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();
document.addEventListener('shopify:section:load', boot);
